import { Link } from "react-router-dom";
import Button from "../../../ui/Button/Button";
import { useMutation, useQuery } from "urql";
import { graphql } from "../../../__generated__/gql";
import Page from "../../Page";
import urls from "../../../routes";
import styles from "./Framer.module.scss";
import Card from "../../../ui/Card/Card";
import TextInput from "../../../ui/TextInput/TextInput";
import SettingsSection from "../../Settings/components/SettingsSection";
import IconButton from "../../../ui/IconButton/IconButton";
import { Copy } from "../../../ui/Icon/Icon";
import { useState } from "react";
import SectionHeading from "../../../layout/SectionHeading/SectionHeading";
import Badge from "../../../ui/Badge/Badge";

const FramerConnectionQuery = graphql(/* GraphQL */ `
  query framerConnection {
    framerConnection {
      id
      apiKey
      connected
    }
  }
`);

const SetupFramerConnectionMutation = graphql(/* GraphQL */ `
  mutation setupFramerConnection {
    setupFramerConnection {
      id
      apiKey
      connected
    }
  }
`);

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <IconButton
      onMouseLeave={() => setCopied(false)}
      aria-label={copied ? "Copied!" : "Copy to clipboard"}
      IconComponent={Copy}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
      }}
    />
  );
}

function Framer() {
  const [framerConnectionResponse, refetchFramerConnection] = useQuery({
    query: FramerConnectionQuery,
  });
  const [, setupFramerConnection] = useMutation(SetupFramerConnectionMutation);

  const { apiKey, connected } =
    framerConnectionResponse.data?.framerConnection || {};

  return (
    <Page
      title="Framer"
      documentTitle="Framer setup"
      breadcrumbs={[
        { url: urls.publishing.connections.root, name: "Publishing" },
      ]}
    >
      <div className={styles.container}>
        <Card className={styles.card}>
          <div>
            <SectionHeading
              className={styles.header}
              title="Framer (Sync)"
              titleElement={
                connected ? (
                  <Badge color="success">Connected</Badge>
                ) : (
                  <Badge color="default">Not Connected</Badge>
                )
              }
              description={
                <>
                  Setup syncing with{" "}
                  <Link
                    to="https://zapier.com/developer/public-invite/191882/537fbaadefcf7c1e4b6fe05145ff6696/"
                    target="_blank"
                  >
                    our Framer plugin
                  </Link>
                  .
                  {/* For help, you can check out our{" "}
                  <Link to="#">Zapier setup guide.</Link> */}
                </>
              }
            />
            <SettingsSection
              label="API Key"
              description="Your API key to connect Framer with Macaw"
            >
              <div className={styles.inputRow}>
                <TextInput
                  disabled
                  className={styles.input}
                  value={apiKey ?? "-"}
                  rightSection={apiKey ? <CopyButton text={apiKey} /> : null}
                />
                <Button
                  variant="primary"
                  onClick={async () => {
                    await setupFramerConnection({});

                    refetchFramerConnection({
                      requestPolicy: "network-only",
                    });
                  }}
                >
                  {apiKey ? "Regenerate" : "Generate"}
                </Button>
              </div>
            </SettingsSection>
          </div>
        </Card>
      </div>
    </Page>
  );
}

export default Framer;
